import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaEye, FaEyeSlash, FaUser, FaPhone, FaBuilding, FaMapMarkerAlt, FaLock } from 'react-icons/fa';
import { useToast } from '../components/Toast';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const toast = useToast();
  const [formData, setFormData] = useState({
    phoneNumber: '',
    name: '',
    society: '',
    address: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Phone number validation
    const cleanPhone = formData.phoneNumber.replace(/\D/g, '');
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (cleanPhone.length !== 10) {
      newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
    } else if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
      newErrors.phoneNumber = 'Please enter a valid Indian mobile number';
    }
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters long';
    }
    
    // Society validation
    if (!formData.society.trim()) {
      newErrors.society = 'Society/Complex name is required';
    }
    
    // Address validation
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }
    
    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    // Address validation (already handled above)
    // Tower, Flat, and Sector are part of the address field
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors above');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await register({
        phoneNumber: formData.phoneNumber.replace(/\D/g, ''),
        name: formData.name.trim(),
        society: formData.society.trim(),
        address: formData.address.trim(),
        password: formData.password
      });
      
      if (result.success) {
        toast.success('Registration successful! Welcome to our food ordering app.');
        navigate('/products');
      } else {
        toast.error(result.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, '');
    // Only allow 10 digits for Indian numbers
    if (numbers.length > 10) {
      return numbers.slice(0, 10);
    }
    return numbers;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, phoneNumber: formatted });
    if (errors.phoneNumber) {
      setErrors({ ...errors, phoneNumber: '' });
    }
  };

  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          <div className="card mt-5">
            <div className="card-body p-6">
              <div className="text-center mb-6">
                <h1 className="h2 mb-2">Create Account</h1>
                <p className="text-secondary">Join our food ordering community</p>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="phoneNumber">
                    <FaPhone className="me-2" />
                    Mobile Number <span style={{color: 'red'}}>*</span>
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    className={`form-control ${errors.phoneNumber ? 'is-invalid' : ''}`}
                    value={formData.phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="Enter 10-digit mobile number"
                    maxLength="10"
                    required
                    disabled={loading}
                  />
                  {errors.phoneNumber && (
                    <div className="invalid-feedback d-block">
                      {errors.phoneNumber}
                    </div>
                  )}
                  <small className="form-text text-muted">
                    Enter your 10-digit mobile number (e.g., 9876543210)
                  </small>
                </div>
                
                <div className="form-group">
                  <label htmlFor="name">
                    <FaUser className="me-2" />
                    Full Name <span style={{color: 'red'}}>*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required
                    disabled={loading}
                  />
                  {errors.name && (
                    <div className="invalid-feedback d-block">
                      {errors.name}
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="society">
                    <FaBuilding className="me-2" />
                    Society Name <span style={{color: 'red'}}>*</span>
                  </label>
                  <input
                    type="text"
                    id="society"
                    name="society"
                    className={`form-control ${errors.society ? 'is-invalid' : ''}`}
                    value={formData.society}
                    onChange={handleInputChange}
                    placeholder="Enter your society or complex name"
                    required
                    disabled={loading}
                  />
                  {errors.society && (
                    <div className="invalid-feedback d-block">
                      {errors.society}
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="address">
                    <FaMapMarkerAlt className="me-2" />
                    Address <span style={{color: 'red'}}>*</span>
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Tower, FlatNo., Sector, City, Pincode"
                    rows="3"
                    required
                    disabled={loading}
                  />
                  {errors.address && (
                    <div className="invalid-feedback d-block">
                      {errors.address}
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="password">
                    <FaLock className="me-2" />
                    Password <span style={{color: 'red'}}>*</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Create a password"
                      required
                      disabled={loading}
                      style={{ paddingRight: '1rem' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                      tabIndex={-1}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        right: '0.75rem',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        color: '#6c757d',
                        fontSize: '1.1rem',
                        cursor: 'pointer',
                        outline: 'none',
                        zIndex: 2
                      }}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {errors.password && (
                    <div className="invalid-feedback d-block">
                      {errors.password}
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="confirmPassword">
                    <FaLock className="me-2" />
                    Confirm Password <span style={{color: 'red'}}>*</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm your password"
                      required
                      disabled={loading}
                      style={{ paddingRight: '1rem' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={loading}
                      tabIndex={-1}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        right: '0.75rem',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        color: '#6c757d',
                        fontSize: '1.1rem',
                        cursor: 'pointer',
                        outline: 'none',
                        zIndex: 2
                      }}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <div className="invalid-feedback d-block">
                      {errors.confirmPassword}
                    </div>
                  )}
                </div>
                
                <button
                  type="submit"
                  className="btn btn-primary w-100 btn-lg"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Creating Account...
                    </>
                  ) : (
                    <>
                      <FaUser className="me-2" />
                      Create Account
                    </>
                  )}
                </button>
              </form>
              
              <div className="text-center mt-6">
                <p className="mb-3">
                  Already have an account?{' '}
                  <Link to="/login" className="text-primary fw-bold">
                    Sign in here
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register; 